import { Component, OnInit, Output, Input, EventEmitter } from '@angular/core';

import { NgForm } from '@angular/forms';

import { translateObject } from '@jsverse/transloco';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { Preferences } from 'app/providers/bridge-api/preferences.service';
import { SemantriaConfiguration } from 'app/providers/semantria-api/configuration.service';
import { Analysis } from 'app/providers/storage-api/analysis.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ProjectCollection } from '../../models/project-collection.model';

@EnableClassTelemetry()
@Component({
  selector: 'app-new-analysis',
  templateUrl: './new-analysis.component.html',
  styleUrls: ['./new-analysis.component.scss'],
  standalone: false,
})
export class NewAnalysisComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() groupConfigurations: SemantriaConfiguration[];
  @Input() collection: ProjectCollection;
  @Input() projectId: string;
  @Input() analysis?: Analysis;

  @Output() modalClosed = new EventEmitter<any>();

  public name: string;
  public configuration: string;
  analysisNameStatus: ErrorObject | null;
  public notes: string;
  public isLoading = { data: true };

  public preferences: Preferences;
  public languages: string[] = [];
  public keyword: string;
  public selectedConfiguration = null;
  public selectedRouteLanguage = null;
  public configCount = 0;
  public lastConfigId: string;
  public defaultConfigName = 'English';
  public lastConfig: SemantriaConfiguration;

  isEditMode = false;

  constructor() {}

  ngOnInit() {
    if (this.analysis) {
      this.isEditMode = true;
      this.isLoading.data = false;
      this.name = this.analysis.displayName;
      this.notes = this.analysis.notes;
    }

    if (this.isEditMode) {
      return;
    }

    this.setDefaultName();
  }

  setDefaultName() {
    if (this.name) {
      return;
    }
    let collectionNameAnalyses = 0;
    this.collection.analyses.forEach((analysis) => {
      if (analysis.displayName.startsWith(this.collection.data.displayName)) {
        if (
          analysis.displayName.length === this.collection.data.displayName.length ||
          analysis.displayName.startsWith(this.collection.data.displayName.concat('_'))
        ) {
          collectionNameAnalyses += 1;
        }
      }
    });
    if (collectionNameAnalyses > 0) {
      this.name = this.collection.data.displayName
        .concat('_')
        .concat(collectionNameAnalyses.toString());
    } else {
      this.name = this.collection.data.displayName;
    }
  }

  public cancel(): void {
    this.modalClosed.emit(null);
  }

  public get configRouteSelected() {
    return this.selectedConfiguration && this.selectedConfiguration.isConfigurationRoute
      ? this.selectedConfiguration.isConfigurationRoute
      : false;
  }

  public displayConfiguration(config?: SemantriaConfiguration): string | undefined {
    return config ? config.name : undefined;
  }

  public canSaveAnalysis(form: NgForm): boolean {
    return !(
      form.invalid ||
      !this.configurationFieldValid ||
      this.analysisNameStatus ||
      this.missingRouteLanguage
    );
  }

  private get missingRouteLanguage() {
    if (this.selectedConfiguration && this.selectedConfiguration.isConfigurationRoute) {
      if (!this.selectedRouteLanguage) {
        return true;
      }
    }
    return false;
  }

  public closeAndAnalyse() {
    const response: any = {
      name: this.name,
      notes: this.notes,
    };
    if (!this.isEditMode) {
      response.isIndustryPack = !!this.selectedConfiguration.isIndustryPack;
      response.isLanguageTemplate = !!this.selectedConfiguration.isLanguageTemplate;
      response.isConfigurationRoute = !!this.selectedConfiguration.isConfigurationRoute;
      response.configurationId = this.selectedConfiguration.id;
      if (response.isIndustryPack) {
        response.configurationId = 'ip:' + this.selectedConfiguration.id;
      }
      if (response.isLanguageTemplate) {
        response.configurationId = 'lt:' + this.selectedConfiguration.id;
      }
      if (response.isConfigurationRoute) {
        response.configurationId = 'cr:' + this.selectedConfiguration.route_id;
        if (this.selectedRouteLanguage && this.selectedRouteLanguage !== 'auto') {
          response.configurationId = response.configurationId + ':' + this.selectedRouteLanguage;
        }
      }
    }
    this.modalClosed.emit(response);
  }

  public onValidateAnalysisName() {
    this.analysisNameStatus = null;
    const translations = translateObject('new-analysis.name-errors');

    if (this.name.length < 1) {
      this.analysisNameStatus = {
        state: EnumErrorStates.ERROR,
        text: translations['required'],
      };
    }

    if (this.collection.analyses.length > 0) {
      let found = this.collection.analyses.find((a) => a.displayName === this.name);
      if (this.analysis && this.analysis.displayName === this.name) {
        found = undefined;
      }
      if (found) {
        this.analysisNameStatus = {
          state: EnumErrorStates.ERROR,
          text: translations['exists'],
        };
      }
    }

    if (this.name.match(/[\t\n]/i) || (this.name.length > 0 && this.name.trim() === '')) {
      this.analysisNameStatus = { state: EnumErrorStates.ERROR, text: translations['invalid'] };
    }

    if (this.name.length > 64) {
      this.analysisNameStatus = {
        state: EnumErrorStates.ERROR,
        text: translations['max-length'],
      };
    }
  }

  public get configurationFieldValid() {
    if (this.isEditMode || this.selectedConfiguration) {
      return true; // no configuration field on edit
    }
    return false;
  }
}
